/** Sidebar navigation — reads user data and pending counts from API. */

import { type ReactNode, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  ApiOutlined,
  AppstoreOutlined,
  AuditOutlined,
  BookOutlined,
  CalendarOutlined,
  DashboardOutlined,
  DesktopOutlined,
  EditOutlined,
  FieldTimeOutlined,
  FolderOpenOutlined,
  FormOutlined,
  FundViewOutlined,
  GlobalOutlined,
  LogoutOutlined,
  QuestionCircleOutlined,
  RightOutlined,
  RiseOutlined,
  RobotOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  SolutionOutlined,
  StockOutlined,
  SwapOutlined,
  TeamOutlined,
  ToolOutlined,
  TrophyOutlined,
  UserOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import { Dropdown } from "antd";
import { useCurrentUser, useCompass } from "../../api/queries";
import { usePermissions } from "../../hooks";
import { useAuthStore } from "@joint/jerry/stores";
import styles from "../../styles/sidebar.module.css";

interface NavItemConfig {
  key: string;
  path: string;
  label: string;
  icon: ReactNode;
  badge?: number;
  children?: NavItemConfig[];
  /** jerry.* permission point required to see this entry (US-H01). */
  requiredPermission?: string;
}

interface NavSection {
  label: string;
  items: NavItemConfig[];
}

interface SidebarProps {
  collapsed?: boolean;
  /** Callback fired when a navigation link is clicked (used to auto-close mobile drawer). */
  onNavigate?: () => void;
}

const NAV_SECTIONS: NavSection[] = [
  {
    label: "工作",
    items: [
      { key: "home", path: "/", label: "工作台", icon: <DashboardOutlined /> },
      { key: "workbench", path: "/workbench", label: "数据库", icon: <DesktopOutlined /> },
      { key: "calendar", path: "/calendar", label: "日历", icon: <CalendarOutlined /> },
      { key: "matters", path: "/matters", label: "客户与事项", icon: <FormOutlined /> },
      { key: "drafts", path: "/drafts", label: "文件起草和审核", icon: <EditOutlined /> },
      { key: "due-diligence", path: "/due-diligence", label: "尽职调查", icon: <AuditOutlined /> },
      // 「要素提取」已收敛进「工具箱」（/quick-tools）作为具体工具入口。
      { key: "meeting-center", path: "/meeting-center", label: "会议助手", icon: <VideoCameraOutlined /> },
      {
        key: "resource-center",
        path: "/resource-center",
        label: "资源中心",
        icon: <FolderOpenOutlined />,
        children: [
          { key: "internal-resources", path: "/resource-center/internal", label: "内部资源", icon: <FolderOpenOutlined /> },
          { key: "external-resources", path: "/resource-center/external", label: "外部资源", icon: <GlobalOutlined /> },
        ],
      },
      { key: "time-entries", path: "/time-entries", label: "工时记录", icon: <FieldTimeOutlined /> },
      {
        key: "useful-info",
        path: "/useful-info",
        label: "IPO 宝库",
        icon: <AppstoreOutlined />,
        children: [
          { key: "ipo-vault-home", path: "/useful-info/vault", label: "宝库总览", icon: <FundViewOutlined /> },
          { key: "ipo", path: "/useful-info/ipo", label: "境内外 IPO", icon: <RiseOutlined /> },
          { key: "overseas-listing", path: "/useful-info/overseas-listing", label: "境外上市备案", icon: <SafetyCertificateOutlined /> },
          { key: "hkex-display", path: "/useful-info/hkex", label: "港交所展示信息", icon: <StockOutlined /> },
          { key: "ipo-intermediary-ranking", path: "/useful-info/ipo-intermediary-ranking", label: "IPO 中介排名", icon: <TrophyOutlined /> },
        ],
      },
      // AI 任务中心：定时任务与长程任务合并为一个入口（path 沿用
      // /scheduled-tasks，深链与通知落点不变；/agent-runs 重定向到此）。
      { key: "ai-tasks", path: "/scheduled-tasks", label: "AI 任务", icon: <RobotOutlined /> },
      { key: "quick-tools", path: "/quick-tools", label: "工具箱", icon: <ToolOutlined /> },
      { key: "help", path: "/help", label: "帮助", icon: <QuestionCircleOutlined /> },
    ],
  },
  {
    label: "管理",
    items: [
      // 「知识库」入口已收敛进「资源中心 › 内部资源 › 学习资料库」（ADR 2026-07-09）。
      { key: "pipelines", path: "/pipelines", label: "工作流", icon: <SwapOutlined /> },
      { key: "project-groups", path: "/project-groups", label: "项目组", icon: <TeamOutlined /> },
      { key: "employees", path: "/employees", label: "员工", icon: <SolutionOutlined />, requiredPermission: "jerry.member:manage" },
      {
        key: "resources",
        path: "/resources",
        label: "AI 广场",
        icon: <BookOutlined />,
        children: [
          { key: "capabilities", path: "/resources/capabilities", label: "AI 能力", icon: <BookOutlined /> },
          { key: "mcp-servers", path: "/resources/mcp-servers", label: "MCP 服务", icon: <ApiOutlined /> },
          { key: "knowledge-resources", path: "/resources/knowledge", label: "业务知识", icon: <BookOutlined /> },
          { key: "pipeline-templates", path: "/resources/pipelines", label: "工作流模板", icon: <SwapOutlined /> },
        ],
      },
    ],
  },
];

/** Check if any child route is currently active. */
function isChildActive(children: NavItemConfig[], pathname: string): boolean {
  return children.some((child) => pathname.startsWith(child.path));
}

export default function Sidebar({ collapsed = false, onNavigate }: SidebarProps) {
  const { data: currentUser } = useCurrentUser();
  const { data: compass } = useCompass();
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useAuthStore((s) => s.logout);
  const { hasPermission } = usePermissions();

  // Track which parent items are expanded (by key).
  // Auto-expand the parent whose child is currently active.
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    NAV_SECTIONS.forEach((section) => {
      section.items.forEach((item) => {
        if (item.children && isChildActive(item.children, location.pathname)) {
          initial.add(item.key);
        }
      });
    });
    return initial;
  });

  const toggleExpand = (key: string, hasChildren: boolean) => {
    if (!hasChildren) return;
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const userName = currentUser?.name ?? "未登录";
  const userInitial = userName.charAt(0);
  const pendingCount = compass?.pendingTasks.length ?? 0;
  const deadlineCount = compass?.upcomingDeadlines.length ?? 0;

  // Identity label (P3-2). The /me response carries no firm grade/职级 field, so
  // we cannot show 合伙人/律师/律师助理 authoritatively here. We surface only the
  // signals we actually have: superuser → 管理员, otherwise a neutral 成员. The
  // previous email-substring guess (`email.includes("assistant")`) mislabelled
  // users such as 律师助理 lin.xiaoyan@jincheng.cn as "律师"; an honest neutral
  // label is preferable to a confidently wrong职级. A correct label requires the
  // backend to add a grade field to /me (see handoff note).
  const userRole = currentUser?.isSuperuser ? "管理员" : "成员";

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const userMenuItems = [
    {
      key: "profile",
      icon: <UserOutlined />,
      label: "个人设置",
      onClick: () => navigate("/profile"),
    },
    {
      key: "settings",
      icon: <SettingOutlined />,
      label: "系统设置",
      onClick: () => navigate("/settings"),
    },
    {
      type: "divider" as const,
    },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: "退出登录",
      danger: true,
      onClick: handleLogout,
    },
  ];

  /** Render a single nav item (leaf or expandable parent). */
  const renderNavItem = (item: NavItemConfig) => {
    const hasChildren = (item.children?.length ?? 0) > 0;
    const expanded = expandedKeys.has(item.key);
    const parentActive = hasChildren && isChildActive(item.children!, location.pathname);

    if (hasChildren) {
      return (
        <div key={item.key}>
          <div
            className={`${parentActive ? styles.navItemActive : styles.navItem} ${collapsed ? styles.navItemCollapsedCenter : ""}`}
            role="button"
            tabIndex={0}
            aria-expanded={expanded}
            aria-label={item.label}
            title={collapsed ? item.label : undefined}
            onClick={() => toggleExpand(item.key, true)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                toggleExpand(item.key, true);
              }
            }}
          >
            <span className={styles.navIcon}>{item.icon}</span>
            {!collapsed && (
              <>
                <span style={{ flex: 1 }}>{item.label}</span>
                <span
                  className={`${styles.navExpandIcon} ${expanded ? styles.navExpandIconOpen : ""}`}
                >
                  <RightOutlined />
                </span>
              </>
            )}
          </div>

          {/* Sub-items */}
          {!collapsed && (
            <div
              className={`${styles.navSubmenu} ${expanded ? styles.navSubmenuOpen : ""}`}
            >
              {item.children!.map((child) => (
                <NavLink
                  key={child.key}
                  to={child.path}
                  end
                  className={({ isActive }) =>
                    isActive
                      ? `${styles.navSubItem} ${styles.navSubItemActive}`
                      : styles.navSubItem
                  }
                  aria-label={child.label}
                  onClick={onNavigate}
                >
                  <span>{child.label}</span>
                </NavLink>
              ))}
            </div>
          )}
        </div>
      );
    }

    // Regular leaf item
    return (
      <NavLink
        key={item.key}
        to={item.path}
        end={item.path === "/"}
        className={({ isActive }) =>
          `${isActive ? styles.navItemActive : styles.navItem}`
        }
        aria-label={item.label}
        title={collapsed ? item.label : undefined}
        onClick={onNavigate}
      >
        <span className={styles.navIcon}>{item.icon}</span>
        {!collapsed && (
          <>
            <span>{item.label}</span>
            {item.key === "matters" && pendingCount > 0 && (
              <span className={styles.navBadge} title={`${pendingCount} 个待办任务`}>{pendingCount}</span>
            )}
            {item.key === "matters" && deadlineCount > 0 && pendingCount === 0 && (
              <span className={styles.navBadge} title={`${deadlineCount} 个即将到期`}>{deadlineCount}</span>
            )}
          </>
        )}
      </NavLink>
    );
  };

  return (
    <aside
      className={`${styles.sidebar} ${collapsed ? styles.sidebarCollapsed : ""}`}
      role="navigation"
      aria-label="主导航"
      data-testid="app-sidebar"
    >
      {/* Brand */}
      <div className={styles.sidebarBrand}>
        <div className={styles.brandMark}>J</div>
        {!collapsed && (
          <div className={styles.brandInfo}>
            <div className={styles.brandName}>律动无限</div>
            <div className={styles.brandOrg}>法律工作台</div>
          </div>
        )}
      </div>

      <div className={styles.sidebarDivider} />

      {/* Navigation */}
      <nav className={styles.sidebarNav}>
        {NAV_SECTIONS.map((section) => {
          // US-H01: hide menu entries the user lacks permission for (the
          // route guard still denies direct navigation).
          const visibleItems = section.items.filter(
            (item) => !item.requiredPermission || hasPermission(item.requiredPermission),
          );
          if (visibleItems.length === 0) return null;
          return (
            <div key={section.label}>
              {!collapsed && (
                <div className={styles.navSectionLabel}>{section.label}</div>
              )}
              {visibleItems.map((item) => renderNavItem(item))}
            </div>
          );
        })}
      </nav>

      {/* User */}
      <div className={styles.sidebarBottom}>
        <div className={styles.sidebarUserRow}>
          <Dropdown
            menu={{ items: userMenuItems }}
            trigger={collapsed ? ["click"] : ["click"]}
            placement="topLeft"
          >
            <div
              className={styles.sidebarUser}
              role="button"
              aria-label="用户菜单"
              tabIndex={0}
            >
              <div className={styles.userAvatar}>{userInitial}</div>
              {!collapsed && (
                <div className={styles.userInfo}>
                  <div className={styles.userName}>{userName}</div>
                  <div className={styles.userRole}>{userRole}</div>
                </div>
              )}
            </div>
          </Dropdown>
        </div>
      </div>
    </aside>
  );
}
